import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../services/api";
// The session itself lives in an httpOnly cookie owned by the API.
// localStorage only caches the (non-sensitive) profile for instant UI;
// GET /admin/auth/me is the source of truth on boot.
const USER_KEY = "admin_user";
const AuthContext = createContext({});
function decodeUser(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const nameParts = String(payload.name || "Admin").split(" ");
        return {
            id: payload.id || payload.sub || "",
            name: nameParts[0] || "Admin",
            surname: nameParts.slice(1).join(" "),
            email: payload.email || "",
            role: payload.role || "admin",
        };
    }
    catch {
        return null;
    }
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem(USER_KEY);
            return raw ? JSON.parse(raw) : null;
        }
        catch {
            return null;
        }
    });
    // Validate the cached session against the cookie on boot. A 401 is handled
    // by the api interceptor (clears cache, redirects to login).
    useEffect(() => {
        if (!localStorage.getItem(USER_KEY))
            return;
        api
            .get("/admin/auth/me")
            .then((r) => {
            const u = r.data.user;
            const fresh = {
                id: u.id || u._id || "",
                name: u.name,
                surname: u.surname,
                email: u.email,
                role: u.role,
                profile_picture_url: u.profile_picture_url,
            };
            localStorage.setItem(USER_KEY, JSON.stringify(fresh));
            setUser(fresh);
        })
            .catch(() => undefined);
    }, []);
    // The token argument is only decoded in memory for the profile display —
    // the httpOnly cookie set by the API is the real session.
    function login(t) {
        const decoded = decodeUser(t);
        if (decoded)
            localStorage.setItem(USER_KEY, JSON.stringify(decoded));
        setUser(decoded);
    }
    function logout() {
        api.post("/admin/auth/logout").catch(() => undefined);
        localStorage.removeItem(USER_KEY);
        setUser(null);
    }
    function updateUser(patch) {
        setUser((u) => {
            if (!u)
                return u;
            const updated = { ...u, ...patch };
            localStorage.setItem(USER_KEY, JSON.stringify(updated));
            return updated;
        });
    }
    return React.createElement(AuthContext.Provider, { value: { user, isAuthenticated: !!user, login, logout, updateUser } }, children);
}
export function useAuth() {
    return useContext(AuthContext);
}
